import React, { useState, useEffect } from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button, 
  Typography, 
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Box,
  Tabs,
  Tab
} from '@mui/material';
import { GetApp as DownloadIcon } from '@mui/icons-material';
import apiClient from '../services/api/client';
import { snowflakeApi } from '../utils/api';

interface FilePreviewDialogProps {
  open: boolean;
  onClose: () => void;
  filePath: string;
  fileName: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`file-preview-tabpanel-${index}`}
      aria-labelledby={`file-preview-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 2 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const FilePreviewDialog: React.FC<FilePreviewDialogProps> = ({ 
  open, 
  onClose, 
  filePath, 
  fileName 
}) => {
  const [csvData, setCsvData] = useState<string[][]>([]);
  const [headers, setHeaders] = useState<string[]>([]);
  const [jsonData, setJsonData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fileType, setFileType] = useState<'csv' | 'json'>('csv');
  const [tabValue, setTabValue] = useState(0);
  
  // Pagination for CSV view
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  useEffect(() => {
    if (open && filePath) {
      // Determine file type based on extension
      if (fileName.toLowerCase().endsWith('.json')) {
        setFileType('json');
      } else {
        setFileType('csv');
      }
      fetchFileContent();
    }
  }, [open, filePath, fileName]);
  
  const fetchFileContent = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await apiClient.get('/api/v1/storage/get', {
        params: {
          path: filePath,
          container: 'client-data'
        }
      });
      
      const fileContent = response.data;
      
      // Check if content is empty
      if (!fileContent || typeof fileContent !== 'string' && !fileContent.topics) {
        setError('The file appears to be empty or in an invalid format.');
        setLoading(false);
        return;
      }
      
      if (fileType === 'json') {
        // Process JSON data
        try {
          // If it's already a parsed object
          if (typeof fileContent === 'object') {
            setJsonData(fileContent);
          } else {
            // If it's a string, parse it
            const parsedJson = JSON.parse(fileContent);
            setJsonData(parsedJson);
          }
        } catch (jsonErr) {
          console.error('Error parsing JSON:', jsonErr);
          setError('Failed to parse JSON data. The file might not be in valid JSON format.');
        }
      } else {
        // Process CSV data
        const csvContent = fileContent;
        
        if (!csvContent || typeof csvContent !== 'string' || csvContent.trim() === '') {
          setError('The CSV file appears to be empty or not in valid format.');
          setLoading(false);
          return;
        }
        
        const lines = csvContent.split('\n');
        const parsedData: string[][] = [];
        
        // Parse each line
        lines.forEach((line: string, index: number) => {
          if (line.trim().length === 0) return;
          
          // Simple CSV parsing - split by comma while preserving quoted values
          let row: string[] = [];
          let inQuote = false;
          let currentValue = '';
          
          for (let i = 0; i < line.length; i++) {
            const char = line[i];
            
            if (char === '"' && (i === 0 || line[i-1] !== '\\')) {
              inQuote = !inQuote;
            } else if (char === ',' && !inQuote) {
              // End of value
              row.push(currentValue);
              currentValue = '';
            } else {
              currentValue += char;
            }
          }
          
          // Don't forget the last value
          if (currentValue) {
            row.push(currentValue);
          }
          
          parsedData.push(row);
        });
        
        // Check if we parsed any data beyond headers
        if (parsedData.length <= 1) {
          setError('The CSV file does not contain any data rows.');
          setHeaders(parsedData.length > 0 ? parsedData[0] : []);
          setCsvData([]);
        } else {
          // Extract headers (first row)
          setHeaders(parsedData[0]);
          setCsvData(parsedData.slice(1).filter(row => row.some(cell => cell.trim() !== '')));
        }
      }
      
      setLoading(false);
    } catch (err) {
      console.error('Error fetching file content:', err);
      setError(`Failed to load ${fileType.toUpperCase()} preview. Please try downloading the file instead.`);
      setLoading(false);
    }
  };
  
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };
  
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };
  
  const handleDownload = () => {
    if (!filePath) return;
    
    const downloadUrl = `${snowflakeApi.defaults.baseURL || ''}/api/v1/storage/download?path=${encodeURIComponent(filePath)}&container=client-data`;
    window.open(downloadUrl, '_blank');
  };
  
  // Display empty cells for potentially uneven rows
  const getDisplayValue = (row: string[], index: number) => {
    return row[index] !== undefined ? row[index] : '';
  };

  // Helper to render JSON data
  const renderJsonData = (data: any, depth: number = 0) => {
    if (data === null) return <span style={{ color: '#666' }}>null</span>;
    if (data === undefined) return <span style={{ color: '#666' }}>undefined</span>;
    
    if (typeof data === 'object') {
      if (Array.isArray(data)) {
        return (
          <Box sx={{ ml: depth * 2, mb: 1 }}>
            <Typography component="span" sx={{ color: '#666' }}>[</Typography>
            <Box sx={{ ml: 2 }}>
              {data.map((item, index) => (
                <Box key={index} sx={{ display: 'flex' }}>
                  {renderJsonData(item, depth + 1)}
                  {index < data.length - 1 && <Typography component="span" sx={{ color: '#666' }}>,</Typography>}
                </Box>
              ))}
            </Box>
            <Typography component="span" sx={{ color: '#666' }}>]</Typography>
          </Box>
        );
      } else {
        const entries = Object.entries(data);
        return (
          <Box sx={{ ml: depth * 2, mb: 1 }}>
            <Typography component="span" sx={{ color: '#666' }}>{`{`}</Typography>
            <Box sx={{ ml: 2 }}>
              {entries.map(([key, value], index) => (
                <Box key={key} sx={{ display: 'flex' }}>
                  <Typography component="span" sx={{ color: '#4169E1', fontWeight: 'bold', mr: 1 }}>
                    "{key}":
                  </Typography>
                  {renderJsonData(value, depth + 1)}
                  {index < entries.length - 1 && <Typography component="span" sx={{ color: '#666' }}>,</Typography>}
                </Box>
              ))}
            </Box>
            <Typography component="span" sx={{ color: '#666' }}>{`}`}</Typography>
          </Box>
        );
      }
    } else if (typeof data === 'string') {
      return <Typography component="span" sx={{ color: '#118811' }}>"{data}"</Typography>;
    } else if (typeof data === 'number' || typeof data === 'boolean') {
      return <Typography component="span" sx={{ color: '#0000FF' }}>{String(data)}</Typography>;
    } else {
      return <Typography component="span">{String(data)}</Typography>;
    }
  };

  // For JSON, show both raw and formatted views
  const renderJsonContent = () => {
    if (!jsonData) return null;

    return (
      <Box sx={{ width: '100%' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="json view tabs">
            <Tab label="Formatted" />
            <Tab label="Raw" />
          </Tabs>
        </Box>
        <TabPanel value={tabValue} index={0}>
          <Paper sx={{ p: 2, maxHeight: '60vh', overflow: 'auto' }}>
            {renderJsonData(jsonData)}
          </Paper>
        </TabPanel>
        <TabPanel value={tabValue} index={1}>
          <Paper sx={{ p: 2, maxHeight: '60vh', overflow: 'auto' }}>
            <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {JSON.stringify(jsonData, null, 2)}
            </pre>
          </Paper>
        </TabPanel>
      </Box>
    );
  };
  
  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="lg" 
      fullWidth 
      aria-labelledby="file-preview-dialog-title"
    >
      <DialogTitle id="file-preview-dialog-title">
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Preview: {fileName}</Typography>
          {/* <Button 
            variant="outlined" 
            size="small" 
            startIcon={<DownloadIcon />}
            onClick={handleDownload}
          >
            Download
          </Button> */}
        </Box>
      </DialogTitle>
      
      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress size={40} />
          </Box>
        ) : error ? (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography color="error" paragraph>{error}</Typography>
            {/* <Button 
              variant="outlined" 
              startIcon={<DownloadIcon />}
              onClick={handleDownload}
            >
              Download File Instead
            </Button> */}
          </Box>
        ) : fileType === 'json' ? (
          renderJsonContent()
        ) : csvData.length === 0 ? (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography paragraph>This file appears to be empty or contains only headers.</Typography>
            {/* <Button 
              variant="outlined" 
              startIcon={<DownloadIcon />}
              onClick={handleDownload}
            >
              Download File Instead
            </Button> */}
          </Box>
        ) : (
          <TableContainer component={Paper}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {headers.map((header, index) => (
                    <TableCell key={index}>{header}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {csvData
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row, rowIndex) => (
                    <TableRow key={rowIndex} hover>
                      {headers.map((_, cellIndex) => (
                        <TableCell key={cellIndex}>
                          {getDisplayValue(row, cellIndex)}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      
      <DialogActions sx={{ justifyContent: 'space-between', px: 3 }}>
        {fileType === 'csv' && csvData.length > 0 && (
          <TablePagination
            component="div"
            count={csvData.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
          />
        )}
        {fileType === 'json' && (
          <Box />
        )}
        <Button onClick={onClose} color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FilePreviewDialog;
